import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { LAYER } from '../lib/layer.ts';
import { cx } from '../lib/styled.tsx';
import { popupArrow, popupTransition } from '../lib/surfaces.ts';

type PositionerProps = ComponentProps<typeof BaseTooltip.Positioner>;

export interface TooltipProps extends ComponentProps<typeof BaseTooltip.Root> {
  /** The element the tooltip describes; it becomes the trigger. */
  children: ReactElement;
  content: ReactNode;
  side?: PositionerProps['side'];
  align?: PositionerProps['align'];
  sideOffset?: PositionerProps['sideOffset'];
  className?: string;
}

/**
 * A short visual label on hover or focus, for controls whose meaning is not
 * already on screen — an icon-only button, most often.
 *
 * Visual only: Base UI deliberately leaves the popup out of the accessibility
 * tree, because a tooltip reaches neither touch nor screen reader users. Give
 * the trigger its own `aria-label` matching the tooltip text, and if the content
 * is something a user actually needs, put it in a {@link Popover} with
 * `openOnHover` — or on the page — instead.
 *
 * Wrap a group of tooltips in {@link TooltipProvider} to share one delay, so
 * moving along a toolbar does not re-open the delay at every button.
 */
export function Tooltip({
  children,
  content,
  side = 'top',
  align = 'center',
  sideOffset = 8,
  className,
  ...rootProps
}: TooltipProps) {
  return (
    <BaseTooltip.Root {...rootProps}>
      <BaseTooltip.Trigger render={children} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner
          align={align}
          className={LAYER.popup}
          side={side}
          sideOffset={sideOffset}
        >
          <BaseTooltip.Popup
            className={cx(
              'rounded-sm bg-ink px-3 py-2 text-sm text-surface shadow-popup',
              popupTransition,
              className,
            )}
          >
            <BaseTooltip.Arrow className={cx(popupArrow, 'before:bg-ink')} />
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}

/** Shares one open/close delay across every tooltip beneath it. */
export const TooltipProvider = BaseTooltip.Provider;
